import { Fragment, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { useAppTranslation } from "@/i18n/useAppTranslation";

type Props = {
  title: ReactNode;
  lead?: ReactNode;
  crumb: string;
  /** Levels between Home and the current page, e.g. Blog on an article. */
  trail?: readonly { label: string; to: string }[];
  actions?: ReactNode;
  aside?: ReactNode;
  children?: ReactNode;
};

/** Opening block for inner pages: breadcrumb, H1, lead, actions and an optional visual. */
export function PageHero({ title, lead, crumb, trail = [], actions, aside, children }: Props) {
  const { t } = useAppTranslation("common");
  return (
    <section
      aria-labelledby="page-heading"
      className="light-pool relative overflow-hidden [--pool-x:85%] [--pool-y:20%]"
    >
      <div
        className={`mx-auto grid max-w-7xl gap-12 px-4 pb-16 pt-28 sm:px-6 sm:pt-32 lg:px-8 lg:pb-24 ${
          aside ? "lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:gap-16" : ""
        }`}
      >
        <div className={aside ? "" : "max-w-4xl"}>
          <nav aria-label={t("breadcrumb")} className="hero-rise">
            <ol className="flex flex-wrap items-center gap-1.5 text-sm text-ink-muted">
              <li>
                <Link to="/" className="rounded transition-colors hover:text-brand-primary">
                  {t("home")}
                </Link>
              </li>
              <li aria-hidden="true">
                <ChevronRight className="size-3.5" />
              </li>
              {trail.map((item) => (
                <Fragment key={item.to}>
                  <li>
                    <Link to={item.to} className="rounded transition-colors hover:text-brand-primary">
                      {item.label}
                    </Link>
                  </li>
                  <li aria-hidden="true">
                    <ChevronRight className="size-3.5" />
                  </li>
                </Fragment>
              ))}
              <li aria-current="page" className="font-medium text-brand-primary">
                {crumb}
              </li>
            </ol>
          </nav>
          <h1
            id="page-heading"
            className="hero-rise mt-6 font-display text-[clamp(2.4rem,5.4vw,4.25rem)] font-semibold leading-[1.04] tracking-[-0.028em] text-brand-primary"
            style={{ ["--delay" as string]: "60ms" }}
          >
            {title}
          </h1>
          {lead ? (
            <div
              className="hero-rise mt-6 max-w-2xl text-lg leading-relaxed text-ink-soft sm:text-xl sm:leading-relaxed"
              style={{ ["--delay" as string]: "120ms" }}
            >
              {lead}
            </div>
          ) : null}
          {actions ? (
            <div
              className="hero-rise mt-9 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-6"
              style={{ ["--delay" as string]: "180ms" }}
            >
              {actions}
            </div>
          ) : null}
          {children}
        </div>
        {aside ? (
          <div className="hero-rise" style={{ ["--delay" as string]: "150ms" }}>
            {aside}
          </div>
        ) : null}
      </div>
    </section>
  );
}
